import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { auth } from "./firebase/firebase";
import { signInWithPopup, signInWithRedirect, getRedirectResult, GoogleAuthProvider, updateProfile } from "firebase/auth";
import AlreadyPlayedScreen from "./AlreadyPlayedScreen";
import { checkHasPlayedDaily } from "./firebase/hasPlayedDaily";

const provider = new GoogleAuthProvider();

// Google Auth Provider

const TitleScreen = ({ onStart, displayName, setDisplayName, currentDay, setActivePanel, setPreviousPanel, autoShowAlreadyPlayed, setAutoShowAlreadyPlayed, authChecked, authenticatedUser = null, testData = null }) => {
  const [showAlreadyPlayedScreen, setShowAlreadyPlayedScreen] = useState(() => testData?.showAlreadyPlayedScreen ?? false);
  const [user, setUser] = useState(() => testData?.user ?? authenticatedUser);
  const [showNameInput, setShowNameInput] = useState(() => testData?.showNameInput ?? false);
  const [tempName, setTempName] = useState(() => testData?.tempName ?? "");
  const [isGoogleFlow, setIsGoogleFlow] = useState(() => testData?.isGoogleFlow ?? false);
  const [nameError, setNameError] = useState("");
  const [isStarting, setIsStarting] = useState(false);

  const startSoundRef = useRef(null);
  const playCheckRef = useRef({ key: null, result: null, promise: null });
  const startInFlightRef = useRef(false);

  useEffect(() => {
    const audio = new Audio("/sounds/startextrasound.mp3");
    audio.preload = "auto";
    startSoundRef.current = audio;
  }, []);

  useEffect(() => {
    if (!testData) setUser(authenticatedUser);
  }, [authenticatedUser, testData]);

  const resolveHasPlayed = useCallback((uid) => {
    const key = `${uid}:${currentDay}`;
    const cached = playCheckRef.current;
    if (cached.key === key && cached.result !== null) return Promise.resolve(cached.result);
    if (cached.key === key && cached.promise) return cached.promise;

    const promise = checkHasPlayedDaily(uid, currentDay)
      .catch((error) => {
        console.warn("Daily play check failed; allowing the game to start", error);
        return false;
      })
      .then((result) => {
        playCheckRef.current = { key, result, promise: null };
        return result;
      });
    playCheckRef.current = { key, result: null, promise };
    return promise;
  }, [currentDay]);

  const handleStart = useCallback(async (explicitUser = null) => {
    if (startInFlightRef.current) return false;
    const startUser = explicitUser || user;
    const uid = startUser?.uid || localStorage.getItem("guestUid");

    if (!uid) return false;
    if (!currentDay) {
      console.warn("Attempted start before currentDay loaded");
      return false;
    }

    startInFlightRef.current = true;
    setIsStarting(true);
    try {
      const alreadyPlayed = await resolveHasPlayed(uid);
      if (alreadyPlayed) {
        setShowNameInput(false);
        setShowAlreadyPlayedScreen(true);
        return false;
      }

      if (startSoundRef.current) {
        startSoundRef.current.currentTime = 0;
        startSoundRef.current.play().catch(() => { });
      }

      onStart();
      return true;
    } finally {
      startInFlightRef.current = false;
      setIsStarting(false);
    }
  }, [currentDay, onStart, resolveHasPlayed, user]);

  // Auto-show AlreadyPlayedScreen when returning from stats/leaderboard
  useEffect(() => {
    if (testData) return undefined;

    if (autoShowAlreadyPlayed) {
      setShowAlreadyPlayedScreen(true);
      setAutoShowAlreadyPlayed(false); // Reset the flag
    }
  }, [autoShowAlreadyPlayed, setAutoShowAlreadyPlayed, testData]);

  // Handle Redirect Result (Mobile)
  useEffect(() => {
    if (testData) return undefined;

    const checkRedirect = async () => {
      try {
        const result = await getRedirectResult(auth);

        if (result && result.user) {
          const user = result.user;
          const fullName = user.displayName || "";
          const firstName = fullName.split(" ")[0];

          // Update Firebase Profile safely
          if (firstName && firstName !== fullName) {
            try {
              await updateProfile(user, { displayName: firstName });
            } catch (pErr) {
              console.warn("Profile update failed during redirect handling", pErr);
            }
          }

          setUser(user);

          // Re-evaluate name for local logic
          const finalName = firstName || user.displayName;

          // Auto-start for seamless experience
          setDisplayName(finalName);
          localStorage.setItem("displayName", finalName);
          setIsGoogleFlow(true);

          // 🚀 Trigger Start Immediately
          handleStart(user);
        }
      } catch (error) {
        console.error("Redirect login failed", error);
      }
    };
    checkRedirect();
  }, [handleStart, setDisplayName, testData]);

  // 🛡️ Auto-Check: If already played today, show screen immediately
  useEffect(() => {
    if (testData) return undefined;

    const autoCheck = async () => {
      // Wait for Auth + Date
      if (!currentDay) return;

      const uid = user?.uid || localStorage.getItem("guestUid");
      if (!uid) return;

      // Check without triggering game start
      const played = await resolveHasPlayed(uid);
      if (played) {
        setShowAlreadyPlayedScreen(true);
      }
    };

    if (authChecked || localStorage.getItem("guestUid")) {
      autoCheck();
    }
  }, [user, currentDay, authChecked, resolveHasPlayed, testData]);

  const handleGoogleLogin = async () => {
    // 🛡️ Strategy: Try Popup first for everyone (Works on many modern mobile browsers now)
    // If it fails (popup blocker), fall back to Redirect.
    // This matches the logic in MyStatsPage.jsx which is confirmed working.
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const fullName = user.displayName || "";
      const firstName = fullName.split(" ")[0];

      // Update Firebase Profile safely
      if (firstName && firstName !== fullName) {
        try {
          await updateProfile(user, { displayName: firstName });
        } catch (pErr) {
          console.warn("Profile update failed", pErr);
        }
      }

      setUser(user);

      const finalName = firstName || user.displayName;

      // Auto-start for seamless experience
      setDisplayName(finalName);
      localStorage.setItem("displayName", finalName);
      setIsGoogleFlow(true);

      // 🚀 Trigger Start Immediately
      handleStart(user);
    } catch (error) {
      console.warn("Popup login failed, attempting redirect...", error);

      // Fallback to redirect check
      if (error.code !== 'auth/cancelled-popup-request') {
        try {
          await signInWithRedirect(auth, provider);
        } catch (redirectErr) {
          console.error("Redirect also failed", redirectErr);
        }
      }
    }
  };

  const handleGuestPlay = () => {
    // 🧠 Generate a unique guest UID if missing
    if (!localStorage.getItem("guestUid")) {
      const guestId = `guest_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
      localStorage.setItem("guestUid", guestId);
      playCheckRef.current = {
        key: `${guestId}:${currentDay}`,
        result: false,
        promise: null,
      };
    }

    setIsGoogleFlow(false);
    setNameError("");
    setShowNameInput(true);
  };

  const handleGuestSubmit = async (event) => {
    event.preventDefault();
    const name = tempName.trim();
    if (!name) {
      setNameError("Enter a display name to continue.");
      return;
    }

    setNameError("");
    setDisplayName(name);
    localStorage.setItem("displayName", name);
    if (isGoogleFlow && user) {
      try {
        await updateProfile(user, { displayName: name });
      } catch (err) {
        console.warn("Failed to update display name on profile", err);
      }
      await handleStart(user);
    } else {
      await handleStart();
    }
  };

  // 🆕 Listen for Enter key to Play Game
  useEffect(() => {
    if (testData) return undefined;

    const handleKeyDown = (e) => {
      // Support Enter, Return, and keyCode 13 for wide compatibility
      const isEnter = e.key === "Enter" || e.key === "Return" || e.keyCode === 13;

      if (isEnter && user && !showNameInput && !showAlreadyPlayedScreen) {
        handleStart();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleStart, showAlreadyPlayedScreen, showNameInput, testData, user]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "radial-gradient(circle at center, #03715f, #034c39)",
        color: "#e7c17f",
        fontFamily: `"Baskerville", "Garamond", "Georgia", serif`,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
        padding: ".5rem",
        textAlign: "center",
        margin: "0",
      }}
    >
      {/* Hello Message */}
      <AnimatePresence>
        {displayName && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            style={{ position: "absolute", top: 20, right: 20, fontSize: "1.2rem" }}
          >
            Hello, {displayName}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Grouped Title & Subtitle Fade-In */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h1
          style={{
            fontSize: "clamp(2.35rem, 14vw, 3.5rem)",
            marginBottom: ".05rem",
            letterSpacing: "clamp(0.5px, 0.4vw, 1.5px)",
            lineHeight: 1,
          }}
        >
          Chronologicle
        </h1>

        <p style={{ marginBottom: "2rem", marginTop: "1rem", fontSize: "1.6rem", maxWidth: "500px" }}>
          Order from oldest to most recent.
        </p>
      </motion.div>

      {/* Buttons - Staggered Fade-In */}
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minHeight: "136px",
      }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            {(user || displayName) ? (
              <motion.button
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                onClick={() => handleStart()}
                style={{ ...buttonStyle, opacity: currentDay && !isStarting ? 1 : 0.6, cursor: currentDay && !isStarting ? "pointer" : "wait" }}
                disabled={!currentDay || isStarting}
                aria-live="polite"
              >
                {isStarting ? "Starting…" : currentDay ? "Play Chronologicle" : "Loading..."}
              </motion.button>
            ) : (
              <>
                <motion.button
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  onClick={handleGoogleLogin}
                  style={{ ...buttonStyle, marginBottom: "1rem" }}
                >
                  Google Sign In
                </motion.button>
                <motion.button
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.6 }}
                  onClick={handleGuestPlay}
                  style={buttonStyle}
                >
                  Play as Guest
                </motion.button>
              </>
            )}
        </div>
      </div>

      <AnimatePresence>
          {showAlreadyPlayedScreen && (
            <AlreadyPlayedScreen
              onGoBack={() => setShowAlreadyPlayedScreen(false)}
              setActivePanel={setActivePanel}
              setAutoShowAlreadyPlayed={setAutoShowAlreadyPlayed}
            />
          )}

          {showNameInput && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "radial-gradient(circle at center, #03715f, #034c39)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 2000,
                padding: "1rem",
              }}
            >
              <form
                onSubmit={handleGuestSubmit}
                noValidate
                role="dialog"
                aria-modal="true"
                aria-labelledby="guest-display-name-label"
                style={{
                  background: "transparent",
                  padding: "2rem",
                  borderRadius: "12px",
                  textAlign: "center",
                  color: "#e7c17f",
                  fontFamily: "Baskerville, serif",
                  width: "100%",
                  maxWidth: "360px",
                }}
              >
                <label
                  id="guest-display-name-label"
                  htmlFor="guest-display-name"
                  className="visually-hidden"
                >
                  Display name
                </label>
                <input
                  id="guest-display-name"
                  type="text"
                  value={tempName}
                  onChange={(e) => {
                    setTempName(e.target.value);
                    if (nameError) setNameError("");
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      e.currentTarget.form?.requestSubmit();
                    }
                  }}
                  maxLength={20}
                  autoComplete="nickname"
                  autoFocus
                  required
                  disabled={isStarting}
                  aria-invalid={Boolean(nameError)}
                  aria-describedby={nameError ? "guest-display-name-error" : undefined}
                  placeholder="Enter display name"
                  style={{
                    fontSize: "1.4rem",
                    padding: "0.75rem 1.25rem",
                    height: "4.7rem",
                    borderRadius: "0.5rem",
                    boxShadow: "none",
                    background: "#082f26",
                    color: "#e7c17f",
                    border: "none",
                    fontFamily: "Baskerville, serif",
                    fontWeight: "bold",
                    textAlign: "center",
                    width: "100%",
                    outline: "none",
                    marginBottom: "1rem",
                    boxSizing: "border-box",
                  }}
                />

                {nameError && (
                  <p
                    id="guest-display-name-error"
                    role="alert"
                    style={{ margin: "-0.25rem 0 1rem", fontSize: "1rem", color: "#fff2d6" }}
                  >
                    {nameError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={isStarting}
                  aria-live="polite"
                  style={{
                    ...buttonStyle,
                    width: "100%",
                    maxWidth: "none",
                    height: "4.7rem",
                    padding: "0.75rem 1.25rem",
                    boxSizing: "border-box",
                    fontSize: "1.4rem",
                  }}
                >
                  {isStarting ? "Starting…" : "Play Chronologicle"}
                </button>
              </form>
            </motion.div>
          )}
      </AnimatePresence>
    </motion.div>
  );
};

const buttonStyle = {
  padding: "1rem 2rem",
  background: "linear-gradient(135deg, #002d21, #00372a)",
  color: "#e7c17f",
  borderRadius: "0.5rem",
  cursor: "pointer",
  fontFamily: "Baskerville, serif",
  width: "100%",
  maxWidth: "300px",
  transition: "transform 0.1s ease, box-shadow 0.1s ease",
  fontWeight: "bold",
  border: "none",
  boxShadow: "none",
  fontSize: "1.4rem",
  margin: "0",
};

export default TitleScreen;
